import React from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Divider,
} from '@mui/material';
import { PaymentStatus, UnforeseenCost } from '../../types/PostProduction';

interface PaymentStatusSectionProps {
  paymentStatus: PaymentStatus;
  editMode: boolean;
  onChange: (field: string, value: any) => void;
  onNestedChange: (field: string, subfield: string, value: any, index?: number) => void;
  onUnforeseenCostAdd: () => void;
}

const PaymentStatusSection: React.FC<PaymentStatusSectionProps> = ({
  paymentStatus,
  editMode,
  onChange,
  onNestedChange,
  onUnforeseenCostAdd,
}) => {
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Payment Status
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={paymentStatus.invoiceReceived}
                  onChange={(e) => onChange('invoiceReceived', e.target.checked)}
                  disabled={!editMode}
                />
              }
              label="Invoice Received"
            />

            {paymentStatus.invoiceReceived && (
              <TextField
                label="Invoice Date"
                type="date"
                fullWidth
                value={paymentStatus.invoiceDate || ''}
                onChange={(e) => onChange('invoiceDate', e.target.value)}
                disabled={!editMode}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Box>

          <TextField
            label="Invoice Number"
            fullWidth
            value={paymentStatus.invoiceNumber || ''}
            onChange={(e) => onChange('invoiceNumber', e.target.value)}
            disabled={!editMode}
            margin="normal"
          />

          <TextField
            label="Amount Paid (€)"
            type="number"
            fullWidth
            value={paymentStatus.amountPaid}
            onChange={(e) => onChange('amountPaid', Number(e.target.value))}
            disabled={!editMode}
            margin="normal"
          />

          <TextField
            label="Payment Date"
            type="date"
            fullWidth
            value={paymentStatus.paymentDate || ''}
            onChange={(e) => onChange('paymentDate', e.target.value)}
            disabled={!editMode}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />

          <FormControl fullWidth sx={{ mb: 3, mt: 2 }}>
            <InputLabel>Payment Method</InputLabel>
            <Select
              value={paymentStatus.paymentMethod}
              onChange={(e) => onChange('paymentMethod', e.target.value)}
              label="Payment Method"
              disabled={!editMode}
            >
              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={paymentStatus.bookkeeping_notified}
                onChange={(e) => onChange('bookkeeping_notified', e.target.checked)}
                disabled={!editMode}
              />
            }
            label="Bookkeeping Notified"
          />

          <TextField
            label="Bookkeeping Notes"
            multiline
            rows={3}
            fullWidth
            value={paymentStatus.bookkeeping_notes || ''}
            onChange={(e) => onChange('bookkeeping_notes', e.target.value)}
            disabled={!editMode}
            margin="normal"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Unforeseen Costs
          </Typography>

          {paymentStatus.unforeseen_costs.map((cost, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    label="Description"
                    fullWidth
                    value={cost.description}
                    onChange={(e) => onNestedChange('unforeseen_costs', 'description', e.target.value, index)}
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Amount (€)"
                    type="number"
                    fullWidth
                    value={cost.amount}
                    onChange={(e) => onNestedChange('unforeseen_costs', 'amount', Number(e.target.value), index)}
                    disabled={!editMode}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}

          {editMode && (
            <Button
              variant="outlined"
              onClick={onUnforeseenCostAdd}
              sx={{ mt: 1 }}
            >
              Add Unforeseen Cost
            </Button>
          )}

          <Box sx={{ mt: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Financial Summary
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2">Additional Costs:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  €{paymentStatus.unforeseen_costs.reduce((sum, cost) => sum + cost.amount, 0).toFixed(2)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2">Total Paid:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2">
                  €{paymentStatus.amountPaid.toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PaymentStatusSection;